import { Dispatch, SetStateAction } from "react";

import { TableColumn } from "@/components/Table/Table";
import { SortParams } from "@/types/types";
import { AssignStatus, InclusionFilterStatus, ListSourceType } from "@/enum/list.enum";
import { ScrapeLevel } from "@/enum/job.enum";

import { ListPerson } from "../../schema/list";
import { ExclusionReasonColumnActions } from "../../utils/columns/exclusionReasonColumn";
import { GeneralTableColumns } from "../TableColumnConfigs/GeneralTableColumns";
import { LiEventTableColumns } from "../TableColumnConfigs/LiEventTableColumns";
import { GoogleMapsTableColumns } from "../TableColumnConfigs/GoogleMapsTableColumns";
import { JobTableColumns } from "../TableColumnConfigs/JobTableColumns";
import { LiContentTableColumns } from "../TableColumnConfigs/LiContentTableColumns";
import { LiPostTableColumns } from "../TableColumnConfigs/LiPostTableColumns";
import { LiCompanyActivityTableColumns } from "../TableColumnConfigs/LiCompanyActivityTableColumns";
import { FundingMaTableColumns } from "../TableColumnConfigs/FundingMaTableColumns";

interface GetColumnsForListTypeParams {
	listType?: ListSourceType;
	/** Current page rows — used to detect which optional Maps/Jobs column groups apply. */
	data: ListPerson[];
	selectAll: boolean;
	setShowAll: Dispatch<SetStateAction<boolean>>;
	toggleSelectAll: () => void;
	selectedItems: number[];
	handleSelectItem: (id: number) => void;
	handleCopyClick: (value: string) => void;
	ts: (key: string) => string;
	sortingInbox: SortParams;
	setShowEdit: ({ id, name }: { id: number; name: string }) => void;
	showEdit: { id: number; name: string } | undefined;
	integrations: { [key: string]: boolean | undefined };
	handleSelectCampaignAndProspect: (
		id: number | string,
		listPersonId: number,
		type?: "internal" | "lemlist"
	) => void;
	assignStatus?: AssignStatus;
	/** Current inclusion-status view — forwarded to the general leads config for the Exclusion column. */
	inclusionStatus?: InclusionFilterStatus[];
	onResearchClick?: (row: ListPerson) => void;
	onOpenDetail?: (row: ListPerson) => void;
	/** Reason-column row actions: Enrich anyway / plain Retry (PREC-3536 Phase 5). */
	exclusionReasonActions?: ExclusionReasonColumnActions;
	/** Resolves a `contacts.` key — the Blacklist bucket's columns share the Blacklist tab's wording. */
	tsContacts?: (key: string) => string;
}

/**
 * Resolve the base column config for a leads table by list type (General / LinkedIn event /
 * profile / post / company-activity / Maps / Jobs / Funding). Extracted from `UnassignedTable`'s
 * `tableColumns` memo to keep that (already-oversized) component shrinking. The shared Outreach
 * column is not part of any config — `insertOutreachColumn` adds it centrally afterwards.
 */
export const getColumnsForListType = ({
	listType,
	data,
	assignStatus,
	inclusionStatus,
	...handlers
}: GetColumnsForListTypeParams): TableColumn[] => {
	const shared = { ...handlers, assignStatus: assignStatus ?? AssignStatus.DRAFT };

	if (listType === ListSourceType.LI_EVENTS) {
		return LiEventTableColumns(shared);
	}

	if (listType === ListSourceType.MAPS) {
		const parts = ["mapBusiness"];

		const checkManagingDirector =
			data.find((r) => !!r.mapBusiness)?.mapBusiness?.map?.mapScrapeType ===
			ScrapeLevel.GET_MANAGING_DIRECTOR;

		if (checkManagingDirector) {
			parts.push("managingDirector");
		}

		const foundPersonInformation = data.find((d) => d.prospect);

		if (foundPersonInformation?.prospect?.id) {
			parts.push("prospect");
		}

		return GoogleMapsTableColumns({ ...shared, parts });
	}

	if (listType === ListSourceType.JOBS) {
		const parts = ["offer"];

		const checkManagingDirector =
			data.find((r) => !!r.offer)?.offer?.job?.jobScrapeType === ScrapeLevel.GET_MANAGING_DIRECTOR;

		if (checkManagingDirector) {
			parts.push("managingDirector");
		}

		const foundPersonInformation = data.find((d) => d.prospect?.id);

		if (foundPersonInformation?.prospect?.id) {
			parts.push("prospect");
		}

		return JobTableColumns({ ...shared, parts });
	}

	if (listType === ListSourceType.FUNDING_ROUNDS) {
		return FundingMaTableColumns(shared);
	}

	if (listType && [ListSourceType.LI_PROFILE].includes(listType)) {
		return LiContentTableColumns(shared);
	}

	if (listType === ListSourceType.LI_POST) {
		return LiPostTableColumns(shared);
	}

	if (listType === ListSourceType.LI_COMPANY_ACTIVITY) {
		return LiCompanyActivityTableColumns(shared);
	}

	return GeneralTableColumns({ ...shared, listType, inclusionStatus });
};
